import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { Typeahead, Highlighter, Token } from 'react-bootstrap-typeahead';
import isEmail from 'validator/lib/isEmail';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import Form from 'react-bootstrap/Form';
const MIN_LEN = 2;
const DEBOUNCE_MS = 300;

function normalizePhone(raw, defaultRegion = 'US') {
  const p = parsePhoneNumberFromString((raw || '').trim(), defaultRegion);
  return p && p.isValid() ? p.number : null; // E.164 format e.g. +15552223333
}

function splitCandidates(raw) {
  return (raw || '')
    .split(/[,;\n]/g)
    .map(s => s.trim())
    .filter(Boolean);
}

function asRecipientOption({ display, address, channel, source, id, avatarUrl, firstname = '', lastname = '', name = '', preferredName = '' }) {
  return {
    key: `${id}`.toLowerCase(),
    label: display,              // primary line
    sublabel: address,           // secondary line (email or phone)
    address,                     // normalized email or E.164 phone
    channel,                     // 'email' | 'sms'
    source,                      // 'contact' | 'user' | 'partner' | etc
    firstname,
    lastname,
    name,
    preferredName,
    id,
    avatarUrl,
  };
}

export default function RecipientPicker({
  contacts,
  channel = 'email',           // 'email' | 'sms'
  value = [],                  // array of recipient options
  onChange = () => {},
  placeholder = 'Type a name, email, or phone…',
  recent = [],                 // optional: recent recipients to show on focus
}) {
    const [query, setQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const debounceRef = useRef(null);

    const validateNew = useCallback((txt) => {
        if (channel === 'email') return isEmail(txt.trim());
        const e164 = normalizePhone(txt);
        return !!e164;
    }, [channel]);

    const toNewOption = useCallback((txt) => {
        if (channel === 'email') {
            const email = txt.trim();
            return asRecipientOption({
                display: email,
                address: email,
                channel: 'email',
                source: 'manual',
                id: `manual:${email}`
            });
        }
        const phone = normalizePhone(txt);
        return asRecipientOption({
            display: phone,
            address: phone,
            channel: 'sms',
            source: 'manual',
            id: `manual:${phone}`
        });
    }, [channel]);

    const options = useMemo( () => {
        setIsLoading(true);
        try {
            // Expecting: [{id, name, emails:[], phones:[], source, avatarUrl}]
            const flat = [];
            for ( const cid in contacts ) {
                const c = contacts[cid];

                const add = (address) => {
                    flat.push(asRecipientOption({
                        display: c.name || address,
                        address,
                        channel,
                        source: c.source,
                        firstname: c.firstname?.toLowerCase() || '',
                        lastname: c.lastname?.toLowerCase() || '',
                        name: c.name?.toLowerCase() || '',
                        preferredName: c.preferredName?.toLowerCase() || '',
                        id: `${cid}::${address}`
                    }));
                };
                
                if ( channel === 'email' && c.email ) {
                    add( c.email );
                }
                if ( channel === 'sms' && ( c.phone || c.phoneMobile || c.phoneHome || c.phoneWork ) ) {
                    // normalize phones to E.164
                    if ( c.phone ) {
                        add( normalizePhone( c.phone ) );
                    }
                    if ( c.phoneMobile ) {
                        add( normalizePhone( c.phoneMobile ) );
                    }
                    if ( c.phoneHome ) {
                        add( normalizePhone( c.phoneHome ) );
                    }
                    if ( c.phoneWork ) {
                        add( normalizePhone( c.phoneWork ) );
                    }
                }
            }
            // de-dupe by key
            //   const dedup = Array.from(new Map(flat.map(o => [o.key, o])).values());
            console.log( 'flat', flat );
            setIsLoading(false);
            return flat;
        } catch (e) {
            if (e.name !== 'AbortError') console.error('recipient search failed', e);
        }
    }, [ contacts, channel ] );

    // Allow paste of multiple recipients to instantly tokenize them.
    const handleInputChange = useCallback((txt) => {
        setQuery(txt);
    }, []);

    const handleKeyDown = useCallback((e) => {
        // Tokenize on Enter/comma/semicolon if valid (and no menu active selection)
        if ( ( e.key === 'Enter' || e.key === ',' || e.key === ';' || e.key === 'Tab' ) && query ) {
            const parts = splitCandidates( query );
            const newTokens = parts
                .map(p => {
                if (channel === 'email' && isEmail(p)) return toNewOption(p);
                if (channel === 'sms') {
                    const ph = normalizePhone(p);
                    return ph ? toNewOption(ph) : null;
                }
                return null;
                })
                .filter(Boolean);

            if (newTokens.length) {
                e.preventDefault();
                const existingKeys = new Set(value.map(v => v.key));
                const dedup = newTokens.filter(t => !existingKeys.has(t.key));
                onChange([...value, ...dedup]);
                setQuery('');
            }
        }
    }, [channel, onChange, query, toNewOption, value]);

    const filterBy = (option, props) => {
        // console.log( 'filtering:', option, props.text );
        if ( !props.text ) {
            return true;
        }
        const q = props.text.toLowerCase();
        
        if ( option.address.includes( q ) ) {
            return true;
        }
        if ( option.name.includes( q ) ) {
            return true;
        }
        return false;
        // // name fields
        // const fields = [
        //   option._search.firstName,
        //   option._search.lastName,
        //   option._search.preferredFirstName,
        //   option.label,
        //   option.sublabel,
        //   ...(option._search.emails || []),
        // ].map(normText);
    
        // // phone fields (search by digits; allow partial)
        // const phoneFields = (option._search.phones || [])
        //   .flatMap(phoneCandidates); // returns full/last7/last4 digit strings
    
        // // Match text-as-text against text fields
        // const textHit = fields.some(f => f.includes(q));
    
        // // If user typed any digits, also try digit matching against phones
        // const qDigits = digits(props.text);
        // const phoneHit = qDigits
        //   ? phoneFields.some(ph => ph.includes(qDigits))
        //   : false;
    
        // return textHit || phoneHit;
    };

    return (
        <Typeahead
            id={`recipient-picker-${channel}`}
            className='recipient-picker'
            multiple
            bodyContainer
            positionFixed
            labelKey='label'
            options={options || []}
            selected={value}
            onChange={(sel) => {
                // RBT marks custom selections with { customOption: true, label: '...' }
                const mapped = sel.map(item => {
                    if (item.customOption) {
                        // only accept if valid
                        if (!validateNew(item.label)) return null;
                        return toNewOption(item.label);
                    }
                    return item;
                }).filter(Boolean);

                // de-dupe by key
                const m = new Map();
                for (const r of mapped) m.set(r.key, r);
                onChange(Array.from(m.values()));
            }}
            placeholder={placeholder}
            minLength={MIN_LEN}
            filterBy={filterBy}
            allowNew
            isValidNewOption={(txt) => validateNew(txt)}
            newSelectionPrefix="Use: "
            renderMenuItemChildren={(option, { text }) => (
                <div className="d-flex align-items-center gap-2">
                    {option.avatarUrl && (
                        <img src={option.avatarUrl} alt="" width="24" height="24" className="rounded-circle" />
                    )}
                    <div className="d-flex flex-column">
                        <div><Highlighter search={text}>{option.label}</Highlighter></div>
                        <small className="text-muted">{option.sublabel}</small>
                    </div>
                    <span className="ms-auto badge bg-light text-dark">{option.source}</span>
                </div>
            )}
            renderToken={(op, props, idx) => (
                <Token
                    key={op.key}
                    option={op}
                    onRemove={props.onRemove}   // ✅ valid here
                    className="me-1"
                    >
                    {op.label} <small>&lt;{op.sublabel}&gt;</small>
                </Token>
            )}
            onKeyDown={handleKeyDown}
            emptyLabel={query.length >= MIN_LEN ? 'No matches' : 'Keep typing…'}
        />
    );
}